import OneSchemaImporter, {
  OneSchemaImporterBaseProps,
} from '@oneschema/react';
import * as Sentry from '@sentry/react';
import React, { RefObject, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { useGenerateJwtQuery } from 'src/graphql';
import { Template } from 'src/hooks/cases/useUploadCases';
import { FeatureFlag, State } from 'src/types';
import { env } from 'src/utils';
import { trackBulkReinviteFailed } from 'src/utils/analytics';

type WrapperWithCallback = HTMLDivElement & {
  __onSuccessCallback?: (data: unknown) => void;
};

export const CasesUploadFile = (
  props: Partial<OneSchemaImporterBaseProps> & {
    steps: string[];
    containerRef?: RefObject<HTMLDivElement | null>;
    isOpen?: boolean;
    setIsOpen?: (isOpen: boolean) => void;
    templateKey: string;
    dynamicFormId?: string | null;
  },
) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const organisation_id = useSelector(
    ({ organisation }: State) => organisation.organisation?.id,
  );
  const features = useSelector((state: State) => state.features);
  const trackingEnabled = isRequiredFeatureEnabled(
    features,
    FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const isModalOpen =
    props.isOpen !== undefined ? props.isOpen : internalIsOpen;
  const setIsModalOpen = props.setIsOpen || setInternalIsOpen;
  const isDynamicTemplate = props.templateKey === Template.CASES_UPLOAD_DYNAMIC;
  const shouldFetchJwt =
    isModalOpen && (!isDynamicTemplate || Boolean(props.dynamicFormId));

  const { data } = useGenerateJwtQuery(
    {
      dynamicFormId: props.dynamicFormId ?? undefined,
    },
    {
      enabled: shouldFetchJwt,
    },
  );

  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  const wrapperRefCallback = (element: HTMLDivElement | null) => {
    wrapperRef.current = element;
    if (element && props.onSuccess) {
      (element as WrapperWithCallback).__onSuccessCallback = props.onSuccess;
    }
  };

  useEffect(() => {
    const wrapper = wrapperRef.current as WrapperWithCallback | null;
    if (wrapper && props.onSuccess) {
      wrapper.__onSuccessCallback = props.onSuccess;
    }
    return () => {
      if (wrapper) {
        delete wrapper.__onSuccessCallback;
      }
    };
  }, [props.onSuccess]);

  return (
    <>
      {data?.generateJwt?.jwt && (
        <div ref={wrapperRefCallback} data-testid="cases-upload-file-wrapper">
          <OneSchemaImporter
            /* managing state from your application */
            onRequestClose={() => {
              setIsModalOpen(false);
            }}
            /* optional config values */
            importConfig={{ type: 'local', metadataOnly: false }}
            devMode={process.env.NODE_ENV !== 'production'}
            className="oneschema-importer"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 2147483301,
            }}
            {...props}
            /* required config values */
            userJwt={data.generateJwt.jwt}
            saveSession={false}
            templateKey={props.templateKey}
            clientId={env.ONE_SCHEMA_CLIENT_ID}
            isOpen={isModalOpen}
            onSuccess={props.onSuccess}
            onError={(e) => {
              Sentry.captureMessage(e);
              if (trackingEnabled && organisation_id) {
                trackBulkReinviteFailed({ organisation_id });
              }
            }}
            onLaunched={({ error }) => {
              if (error) {
                Sentry.captureException(error);
                if (trackingEnabled && organisation_id) {
                  trackBulkReinviteFailed({ organisation_id });
                }
              }
            }}
          />
        </div>
      )}
    </>
  );
};
